import api from './api';
import { GetOrdersResponse, GetOrdersParams, Order } from '@/types/order';

export const orderApi = {
  // Sipariş listesi
  async getOrders(params?: GetOrdersParams): Promise<GetOrdersResponse> {
    try {
      const response = await api.get('/admin/orders', { params });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching orders:', error);
      throw new Error(error.response?.data?.error || 'Siparişler yüklenemedi');
    }
  },

  // Tek sipariş detayı
  async getOrderById(id: string): Promise<Order> {
    try {
      const response = await api.get(`/admin/orders/${id}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching order:', error);
      throw new Error(error.response?.data?.error || 'Sipariş yüklenemedi');
    }
  },

  // Sipariş durumu güncelle
  async updateOrderStatus(id: string, status: string): Promise<void> {
    try {
      await api.put(`/admin/orders/${id}/status`, { status });
    } catch (error: any) {
      console.error('Error updating order status:', error);
      throw new Error(error.response?.data?.error || 'Durum güncellenemedi');
    }
  },

  // Ödeme işlendi olarak işaretle
  async markPaymentProcessed(id: string): Promise<void> {
    try {
      await api.put(`/admin/orders/${id}/payment-processed`);
    } catch (error: any) {
      console.error('Error marking payment processed:', error);
      throw new Error(error.response?.data?.error || 'Ödeme işlenemedi');
    }
  },

  // Havale ödemesini onayla
  async confirmBankTransfer(id: string): Promise<void> {
    try {
      await api.post(`/admin/orders/${id}/confirm-bank-transfer`);
    } catch (error: any) {
      console.error('Error confirming bank transfer:', error);
      throw new Error(error.response?.data?.error || 'Havale onaylanamadı');
    }
  },

  // Sipariş önizleme fotoğrafı yükle
  async uploadOrderPreview(id: string, photoUrl: string): Promise<Order> {
    try {
      const response = await api.post(`/vendor/orders/${id}/preview`, { photoUrl });
      return response.data;
    } catch (error: any) {
      console.error('Error uploading preview:', error);
      throw new Error(error.response?.data?.error || 'Önizleme yüklenemedi');
    }
  },

  // Önizleme onayını manuel güncelle
  async approveOrderPreview(orderId: string): Promise<Order> {
    try {
      const response = await api.post(`/vendor/orders/${orderId}/approve-preview`);
      return response.data;
    } catch (error: any) {
      console.error('Error approving order preview:', error);
      throw new Error(error.response?.data?.error || 'Görsel onaylanamadı');
    }
  },

  // Önizleme onayını güncelle (eski uyumluluk)
  async updatePreviewApproval(id: string, isApproved: boolean): Promise<void> {
    try {
      await api.put(`/admin/orders/${id}/preview-approval`, { isApproved });
    } catch (error: any) {
      console.error('Error updating preview approval:', error);
      throw new Error(error.response?.data?.error || 'Onay güncellenemedi');
    }
  },

  // Vendor ata
  async assignVendor(id: string, vendorId: string): Promise<void> {
    try {
      await api.put(`/admin/orders/${id}/assign-vendor`, { vendorId });
    } catch (error: any) {
      console.error('Error assigning vendor:', error);
      throw new Error(error.response?.data?.error || 'Vendor atanamadı');
    }
  },

  // Siparişe kurye ata
  async assignCourier(orderId: string, courierId: string): Promise<Order> {
    try {
      const response = await api.put(`/admin/orders/${orderId}/assign-courier`, { courierId });
      return response.data;
    } catch (error: any) {
      console.error('Error assigning courier:', error);
      throw new Error(error.response?.data?.error || 'Kurye atanamadı');
    }
  },

  async updateShipmentTrackingUrl(orderId: string, shipmentTrackingUrl: string): Promise<Order> {
    try {
      const response = await api.put(`/admin/orders/${orderId}/shipment-tracking`, { shipmentTrackingUrl });
      return response.data;
    } catch (error: any) {
      console.error('Error updating shipment tracking URL:', error);
      throw new Error(error.response?.data?.error || 'Takip linki güncellenemedi');
    }
  },

  async issueInvoice(orderId: string): Promise<Order> {
    try {
      const response = await api.post(`/admin/orders/${orderId}/invoice/issue`);
      return response.data;
    } catch (error: any) {
      console.error('Error issuing invoice:', error);
      throw new Error(error.response?.data?.error || 'Fatura kesilemedi');
    }
  },

  // Sipariş iptal et
  async cancelOrder(orderId: string, reason?: string): Promise<Order> {
    try {
      const response = await api.put(`/admin/orders/${orderId}/cancel`, { reason });
      return response.data;
    } catch (error: any) {
      console.error('Error canceling order:', error);
      throw new Error(error.response?.data?.error || 'Sipariş iptal edilemedi');
    }
  },

  // Kredi kartı ödemesini POS üzerinden iade/iptal et
  async refundOrder(orderId: string, params: { amount: number; type: 'cancel' | 'refund'; reason?: string }): Promise<void> {
    try {
      await api.post(`/admin/orders/${orderId}/refund`, params);
    } catch (error: any) {
      console.error('Error refunding order:', error);
      throw new Error(error.response?.data?.error || 'İade/iptal işlemi başarısız');
    }
  },

  // Toplu sipariş silme
  async bulkDeleteOrders(orderIds: string[]): Promise<{
    message: string;
    results: {
      orders: number;
      orderFinancials: number;
      paymentTransactions: number;
      accountingLogs: number;
      notifications: number;
      couponUsageLogs: number;
      couponUsedCountUpdates: number;
      storageFiles: number;
      errors: string[];
    };
  }> {
    try {
      const response = await api.post('/admin/orders/bulk-delete', { orderIds });
      return response.data;
    } catch (error: any) {
      console.error('Error bulk deleting orders:', error);
      throw new Error(error.response?.data?.error || 'Toplu silme başarısız');
    }
  },
};
